/**
 * Ảnh báo cáo (tài xế, nhân viên): khung 16:9 + dấu Ngày – Giờ – Vị trí in lên ảnh (yêu cầu anh Tiến 14/10/2026, X-PWA §6).
 * Ảnh không bị cắt: đặt vừa khung (ảnh ngang → 16:9, ảnh dọc → 9:16), phần thừa tô đen, dấu nằm dưới đáy.
 * Hàm thuần để vẽ trên trình duyệt và test được.
 */

/** Cạnh dài tối đa của ảnh báo cáo (px) */
export const REPORT_PHOTO_LONG_EDGE = 1600;

export interface ReportFrame {
  /** Kích thước khung (16:9 hoặc 9:16) */
  width: number;
  height: number;
  /** Vị trí, kích thước ảnh gốc vẽ vào khung (vừa khung, giữ tỷ lệ, căn giữa) */
  dx: number;
  dy: number;
  dw: number;
  dh: number;
}

/** Khung 16:9 (ảnh ngang/vuông) hoặc 9:16 (ảnh dọc), không phóng to quá ảnh gốc, ảnh nằm giữa không bị cắt. */
export function reportFrame(srcW: number, srcH: number, longEdge = REPORT_PHOTO_LONG_EDGE): ReportFrame {
  if (!(srcW > 0) || !(srcH > 0)) throw new Error('Kích thước ảnh không hợp lệ');
  const portrait = srcH > srcW;
  const long = Math.min(longEdge, Math.max(srcW, srcH));
  const short = Math.round((long * 9) / 16);
  const width = portrait ? short : long;
  const height = portrait ? long : short;
  const scale = Math.min(width / srcW, height / srcH);
  const dw = Math.round(srcW * scale);
  const dh = Math.round(srcH * scale);
  return { width, height, dx: Math.round((width - dw) / 2), dy: Math.round((height - dh) / 2), dw, dh };
}

export interface StampInput {
  /** Thời điểm in lên ảnh */
  at: Date;
  /** 'server' = giờ máy chủ; 'device' = giờ điện thoại (không gọi được máy chủ, vd mất mạng) */
  clock: 'server' | 'device';
  lat: number | null;
  lng: number | null;
  /** Sai số GPS (m) */
  accuracyM?: number | null;
  address?: string | null;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** dd/mm/yyyy HH:MM:SS theo giờ Việt Nam (UTC+7, không đổi giờ mùa hè). */
export function formatVnDateTime(at: Date): string {
  const t = new Date(at.getTime() + 7 * 3600_000);
  return `${pad(t.getUTCDate())}/${pad(t.getUTCMonth() + 1)}/${t.getUTCFullYear()} ${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}:${pad(t.getUTCSeconds())}`;
}

export const NO_LOCATION_TEXT = 'Không có vị trí';

/** Các dòng chữ in lên ảnh: ngày giờ, tọa độ (hoặc "Không có vị trí"), địa chỉ nếu có. */
export function stampLines(s: StampInput): string[] {
  const lines = [`${formatVnDateTime(s.at)}${s.clock === 'device' ? ' (giờ điện thoại)' : ''}`];
  const hasPos = s.lat != null && s.lng != null && Number.isFinite(s.lat) && Number.isFinite(s.lng);
  if (!hasPos) lines.push(NO_LOCATION_TEXT);
  else {
    const acc = s.accuracyM != null && Number.isFinite(s.accuracyM) ? ` · ±${Math.max(1, Math.round(s.accuracyM))} m` : '';
    lines.push(`${s.lat!.toFixed(6)}, ${s.lng!.toFixed(6)}${acc}`);
    const addr = s.address?.trim();
    if (addr) lines.push(addr);
  }
  return lines;
}
